import * as React from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
  useDismiss,
  useInteractions,
  useHover,
  safePolygon,
} from '@floating-ui/react'
import { Editor } from '@tiptap/react'
import {
  Maximize2,
  Minimize2,
  Square,
  Type,
  Image as ImageIcon,
  Moon,
  Trash2,
  SquarePen,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui'
import { clamp } from '../../../utils/helpers'
import './inline-image-hover-card.scss'
import './inline-image-hover-card.scss'

interface InlineImageHoverCardProps {
  editor: Editor
  onUpload?: (file: File) => Promise<string>
}

interface ImageData {
  src: string
  alt?: string
  title?: string
  darkSrc?: string
  display?: 'inline' | 'original' | 'block'
  nodePos?: number
}

export function InlineImageHoverCard({
  editor,
  onUpload,
}: InlineImageHoverCardProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [isHoveringCard, setIsHoveringCard] = React.useState(false)
  const [imageElement, setImageElement] = React.useState<HTMLElement | null>(
    null
  )
  const [imageData, setImageData] = React.useState<ImageData | null>(null)
  const [isEditingAlt, setIsEditingAlt] = React.useState(false)
  const [altText, setAltText] = React.useState('')
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    middleware: [
      offset(6),
      flip({
        fallbackPlacements: [
          'bottom',
          'top-start',
          'top-end',
          'bottom-start',
          'bottom-end',
        ],
      }),
      shift({ padding: 5 }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    handleClose: safePolygon({
      blockPointerEvents: false,
      buffer: 1,
    }),
    delay: {
      open: 500,
      close: 0,
    },
    enabled: !!imageElement,
  })

  const dismiss = useDismiss(context)
  const { getFloatingProps } = useInteractions([hover, dismiss])

  React.useEffect(() => {
    if (!editor || !editor.view) return

    const editorDom = editor.view.dom

    const handleMouseEnter = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const img = target.closest(
        'img[data-inline-image]'
      ) as HTMLImageElement | null

      if (!img) return

      // Skip dark mode images in light mode
      if (
        img.classList.contains('inline-image--dark') &&
        !document.documentElement.classList.contains('dark')
      ) {
        return
      }

      // Skip light mode images in dark mode
      if (
        img.classList.contains('inline-image--light') &&
        document.documentElement.classList.contains('dark')
      ) {
        return
      }

      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      const src = img.getAttribute('src') || ''
      const alt = img.getAttribute('alt') || ''
      const title = img.getAttribute('title') || ''
      const darkSrc = img.getAttribute('data-dark-src') || ''
      const display = (img.getAttribute('data-display') || 'inline') as
        | 'inline'
        | 'original'
        | 'block'

      // Find the node position - for node views, we need to find the wrapper
      const nodeViewWrapper = img.closest(
        '[data-node-view-wrapper]'
      ) as HTMLElement | null

      if (!nodeViewWrapper) return

      try {
        const pos = editor.view.posAtDOM(nodeViewWrapper, 0)

        setImageData({ src, alt, title, darkSrc, display, nodePos: pos })
        setImageElement(img)
        setAltText(alt)
        refs.setReference(img)
        setIsOpen(true)
      } catch (error) {
        console.error('Failed to find node position:', error)
      }
    }

    const handleMouseLeave = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const img = target.closest('img[data-inline-image]')

      if (!img) return

      const relatedTarget = e.relatedTarget as HTMLElement | null

      // Check if we're moving to the hover card
      const isMovingToCard = relatedTarget?.closest('.inline-image-hover-card')

      if (!isMovingToCard) {
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        // Delay closing to allow moving to the hover card
        timeoutRef.current = setTimeout(() => {
          if (!isHoveringCard) {
            setImageElement(null)
            setImageData(null)
            setIsOpen(false)
            setIsEditingAlt(false)
          }
        }, 500)
      }
    }

    // Use event delegation
    editorDom.addEventListener('mouseenter', handleMouseEnter, true)
    editorDom.addEventListener('mouseleave', handleMouseLeave, true)

    return () => {
      editorDom.removeEventListener('mouseenter', handleMouseEnter, true)
      editorDom.removeEventListener('mouseleave', handleMouseLeave, true)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [editor, refs, setIsOpen, isHoveringCard])

  const handleDisplayChange = (newDisplay: 'inline' | 'original' | 'block') => {
    if (!imageData || imageData.nodePos === undefined) return

    // Update the node attributes
    editor
      .chain()
      .focus()
      .setNodeSelection(imageData.nodePos)
      .updateAttributes('inlineImage', { display: newDisplay })
      .run()

    setImageData({ ...imageData, display: newDisplay })
  }

  const handleAltTextSave = () => {
    if (!imageData || imageData.nodePos === undefined) return

    // Update the node attributes
    editor
      .chain()
      .focus()
      .setNodeSelection(imageData.nodePos)
      .updateAttributes('inlineImage', { alt: altText })
      .run()

    setImageData({ ...imageData, alt: altText })
    setIsEditingAlt(false)
    toast.success('Alt text updated')
  }

  const handleDelete = () => {
    if (!imageData || imageData.nodePos === undefined) return

    // Delete the inline image node
    editor
      .chain()
      .focus()
      .setNodeSelection(imageData.nodePos)
      .deleteSelection()
      .run()

    setIsOpen(false)
  }

  if (!isOpen || !imageData || !imageElement) {
    return null
  }

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        className='inline-image-hover-card'
        onMouseEnter={() => {
          setIsHoveringCard(true)
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
        }}
        onMouseLeave={() => {
          setIsHoveringCard(false)
          timeoutRef.current = setTimeout(() => {
            setImageElement(null)
            setImageData(null)
            setIsOpen(false)
            setIsEditingAlt(false)
          }, 300)
        }}
      >
        <div className='inline-image-hover-card-content bg-popover'>
          {isEditingAlt ? (
            <div className='inline-image-hover-card-alt-edit space-y-2 !p-2'>
              <Label
                htmlFor='alt-text'
                className='text-sm leading-[14px] font-medium'
              >
                Alt Text
              </Label>
              <Input
                id='alt-text'
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAltTextSave()
                  } else if (e.key === 'Escape') {
                    setIsEditingAlt(false)
                    setAltText(imageData.alt || '')
                  }
                }}
                placeholder='Describe this image...'
                autoFocus
              />
              <div className='inline-image-hover-card-alt-actions'>
                <Button
                  size='sm'
                  variant='ghost'
                  onClick={() => {
                    setIsEditingAlt(false)
                    setAltText(imageData.alt || '')
                  }}
                >
                  Cancel
                </Button>
                <Button size='sm' onClick={handleAltTextSave}>
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className='flex items-center'>
              <div className='inline-image-hover-card-display !border-0 !pb-0'>
                <Button
                  variant='ghost'
                  size='xs'
                  onClick={() => handleDisplayChange('inline')}
                  title='Inline size'
                  className={
                    imageData.display === 'inline'
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground'
                  }
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    className='h-4 w-4 flex-shrink-0'
                  >
                    <g clip-path='currentColor'>
                      <path
                        d='M6 4.00004C6 3.82323 6.07024 3.65366 6.19526 3.52864C6.32029 3.40361 6.48986 3.33337 6.66667 3.33337H9.33333C9.51014 3.33337 9.67971 3.40361 9.80474 3.52864C9.92976 3.65366 10 3.82323 10 4.00004V6.66671C10 6.84352 9.92976 7.01309 9.80474 7.13811C9.67971 7.26314 9.51014 7.33337 9.33333 7.33337H6.66667C6.48986 7.33337 6.32029 7.26314 6.19526 7.13811C6.07024 7.01309 6 6.84352 6 6.66671V4.00004Z'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M2.6665 4.66663H3.33317'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M12.6665 4.66663H13.3332'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M2.6665 10H13.3332'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M2.6665 12.6666H13.3332'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_7909_25574'>
                        <rect width='16' height='16' fill='currentColor' />
                      </clipPath>
                    </defs>
                  </svg>
                </Button>
                <Button
                  variant='ghost'
                  size='xs'
                  onClick={() => handleDisplayChange('original')}
                  title='Original size'
                  className={
                    imageData.display === 'original'
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground'
                  }
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    className='h-4 w-4 flex-shrink-0'
                  >
                    <g clip-path='url(#clip0_7933_9958)'>
                      <path
                        d='M6 4.00004C6 3.82323 6.07024 3.65366 6.19526 3.52864C6.32029 3.40361 6.48986 3.33337 6.66667 3.33337H9.33333C9.51014 3.33337 9.67971 3.40361 9.80474 3.52864C9.92976 3.65366 10 3.82323 10 4.00004V9.66671C10 9.84352 9.92976 10.0131 9.80474 10.1381C9.67971 10.2631 9.51014 10.3334 9.33333 10.3334H6.66667C6.48986 10.3334 6.32029 10.2631 6.19526 10.1381C6.07024 10.0131 6 9.84352 6 9.66671V4.00004Z'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M2.6665 5.66663H3.33317'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M2.6665 8.33337H3.33317'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M12.6665 5.66663H13.3332'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M12.6665 8.33337H13.3332'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M2.6665 12.6666H13.3332'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_7933_9958'>
                        <rect width='16' height='16' fill='currentColor' />
                      </clipPath>
                    </defs>
                  </svg>
                </Button>
                <Button
                  variant='ghost'
                  size='xs'
                  onClick={() => handleDisplayChange('block')}
                  title='Block display'
                  className={
                    imageData.display === 'block'
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground'
                  }
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    className='h-4 w-4 flex-shrink-0'
                  >
                    <g clip-path='url(#clip0_7909_25620)'>
                      <path
                        d='M2.6665 4.00004C2.6665 3.82323 2.73674 3.65366 2.86177 3.52864C2.98679 3.40361 3.15636 3.33337 3.33317 3.33337H11.9998C12.1766 3.33337 12.3462 3.40361 12.4712 3.52864C12.5963 3.65366 12.6665 3.82323 12.6665 4.00004V6.66671C12.6665 6.84352 12.5963 7.01309 12.4712 7.13811C12.3462 7.26314 12.1766 7.33337 11.9998 7.33337H3.33317C3.15636 7.33337 2.98679 7.26314 2.86177 7.13811C2.73674 7.01309 2.6665 6.84352 2.6665 6.66671V4.00004Z'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M2.6665 10H13.3332'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                      <path
                        d='M2.6665 12.6666H13.3332'
                        stroke='currentColor'
                        stroke-width='1.33'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_7909_25620'>
                        <rect width='16' height='16' fill='currentColor' />
                      </clipPath>
                    </defs>
                  </svg>
                </Button>
              </div>

              <Separator
                orientation='vertical'
                className='bg-border mx-1 !h-6'
              />
              <div className='inline-image-hover-card-actions'>
                <Button
                  variant='ghost'
                  size='xs'
                  onClick={() => setIsEditingAlt(true)}
                  title='Edit alt text'
                >
                  <Type className='text-muted-foreground h-4 w-4' />
                </Button>

                <Button
                  variant='ghost'
                  size='xs'
                  onClick={() => {
                    editor.emit('openInlineImageDialog', {
                      replaceMode: true,
                      existingImage: imageData,
                    })
                    setIsOpen(false)
                  }}
                  title='Replace image'
                >
                  <SquarePen className='text-muted-foreground h-4 w-4' />
                </Button>

                <Button
                  variant='ghost'
                  size='xs'
                  onClick={() => {
                    editor.emit('openInlineImageDialog', {
                      replaceMode: true,
                      existingImage: imageData,
                      isDarkMode: true,
                    })
                    setIsOpen(false)
                  }}
                  title='Add dark mode image'
                  className={imageData.darkSrc ? 'has-dark-image' : ''}
                >
                  <Moon className='text-muted-foreground h-4 w-4' />
                </Button>

                <Button
                  variant='ghost'
                  size='xs'
                  onClick={handleDelete}
                  title='Delete image'
                >
                  <Trash2 className='text-muted-foreground h-4 w-4' />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </FloatingPortal>
  )
}
